import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { assertSportsTradingAllowed, type SportsTradingScope } from './sportsTradingGuard';

export type ClassifiedTradeRequestConfig<Data = unknown> = AxiosRequestConfig<Data> & {
    sportsTradingScope: SportsTradingScope;
};

interface ClassifiedTradeRequest<Data> {
    method: 'delete' | 'patch' | 'post' | 'put';
    url: string;
    data?: Data;
    sportsTradingScope: SportsTradingScope;
    config?: AxiosRequestConfig<Data>;
}

/**
 * Typed boundary for trade-mutating requests. Requiring a derived classification here keeps
 * individual callers from relying on an optional, untyped Axios property. The global request
 * interceptor remains a fail-closed backstop for any protected URL that bypasses this gateway.
 */
export async function executeClassifiedTradeRequest<Response, Data = unknown>(
    client: AxiosInstance,
    { method, url, data, sportsTradingScope, config }: ClassifiedTradeRequest<Data>,
): Promise<AxiosResponse<Response>> {
    assertSportsTradingAllowed(sportsTradingScope);
    const classifiedConfig: ClassifiedTradeRequestConfig<Data> = {
        ...config,
        sportsTradingScope,
    };

    switch (method) {
        case 'delete':
            return client.delete<Response>(url, classifiedConfig);
        case 'patch':
            return client.patch<Response>(url, data, classifiedConfig);
        case 'post':
            return client.post<Response>(url, data, classifiedConfig);
        case 'put':
            return client.put<Response>(url, data, classifiedConfig);
    }
}
